import React, { useState, useEffect } from 'react';
import { X, Sparkles, BookOpen, Users, Heart, Palette, MessageSquare, Zap, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const StandardOnboardingModal = ({ 
  isOpen, 
  onClose, 
  onHighlightField,
  onComplete 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { tr } = useLanguage();

  const onboardingSteps = [
    {
      field: 'name',
      icon: Users,
      title: tr('Personalize the Name', 'Personnalisez le prénom'),
      description: tr("Change the child's name anytime to create stories for different children.", "Changez le prénom à tout moment pour créer des histoires pour différents enfants."),
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50'
    },
    {
      field: 'age',
      icon: BookOpen,
      title: tr('Adjust Age Complexity', "Ajustez la complexité selon l'âge"),
      description: tr("Select different age ranges to match story complexity to the child's level.", "Sélectionnez une tranche d'âge pour adapter la complexité au niveau de l'enfant."),
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-50 to-emerald-50'
    },
    {
      field: 'gender',
      icon: Users,
      title: tr('Choose Perspective', 'Choisissez la perspective'),
      description: tr('Switch between boy, girl, or other to customize character perspective.', 'Choisissez garçon, fille ou autre pour personnaliser le personnage.'),
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50'
    },
    {
      field: 'interests',
      icon: Heart,
      title: tr('Change Adventures', 'Changez les aventures'),
      description: tr('Update interests to create completely different story themes and adventures.', "Modifiez les centres d'intérêt pour créer des thèmes et aventures totalement différents."),
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-50 to-red-50'
    },
    {
      field: 'style',
      icon: Palette,
      title: tr('Modify Story Mood', "Modifiez l'ambiance"),
      description: tr('Adjust tone from funny and silly to gentle and educational.', 'Ajustez le ton : drôle et loufoque ou doux et éducatif.'),
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'from-indigo-50 to-purple-50'
    },
    {
      field: 'lesson',
      icon: MessageSquare,
      title: tr('Update Life Lessons', 'Changez les leçons de vie'),
      description: tr('Change the moral or lesson you want the story to teach.', "Changez la morale ou la leçon que l'histoire doit transmettre."),
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'from-teal-50 to-cyan-50'
    }
  ];

  const finalStep = {
    icon: Zap,
    title: tr('Quick Generation', 'Génération rapide'),
    description: tr("Now click 'Create Another Story' to instantly generate new stories with your customized parameters—no more steps needed!", "Cliquez sur 'Créer une autre histoire' pour générer instantanément de nouvelles histoires avec vos paramètres — plus aucune étape nécessaire !"),
    color: 'from-yellow-500 to-orange-500',
    bgColor: 'from-yellow-50 to-orange-50'
  };

  useEffect(() => {
    if (isOpen && currentStep < onboardingSteps.length) {
      const step = onboardingSteps[currentStep];
      onHighlightField(step.field);
    } else if (currentStep >= onboardingSteps.length) {
      onHighlightField(null);
    }
  }, [currentStep, isOpen, onHighlightField]);

  const handleNext = () => {
    setIsAnimating(true);
    setTimeout(() => {
      if (currentStep < onboardingSteps.length) {
        setCurrentStep(prev => prev + 1);
      } else {
        // Final step completed
        localStorage.setItem('standardOnboardingSeen', 'true');
        onComplete();
        onClose();
      }
      setIsAnimating(false);
    }, 200);
  };

  const handleSkip = () => {
    localStorage.setItem('standardOnboardingSeen', 'true');
    onHighlightField(null);
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  const currentStepData = currentStep < onboardingSteps.length 
    ? onboardingSteps[currentStep] 
    : finalStep;

  const Icon = currentStepData.icon;
  const isLastStep = currentStep >= onboardingSteps.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-all duration-300"
        onClick={handleSkip}
      />
      
      {/* Modal */}
      <div className={`relative bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 max-w-md w-full mx-4 transform transition-all duration-300 ${isAnimating ? 'scale-95 opacity-70' : 'scale-100 opacity-100'}`}>
        
        {/* Header with gradient background */}
        <div className={`bg-gradient-to-r ${currentStepData.bgColor} rounded-t-3xl p-6 relative overflow-hidden`}>
          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          {/* Progress indicator */}
          <div className="flex items-center gap-1 mb-4">
            {[...Array(onboardingSteps.length + 1)].map((_, index) => (
              <div 
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index <= currentStep 
                    ? 'bg-white/80 w-8' 
                    : 'bg-white/30 w-4'
                }`}
              />
            ))}
          </div>

          {/* Icon with animation */}
          <div className="flex items-center justify-center mb-4">
            <div className={`relative w-16 h-16 bg-gradient-to-r ${currentStepData.color} rounded-2xl flex items-center justify-center shadow-lg transform transition-all duration-500 ${isAnimating ? 'scale-110 rotate-12' : 'scale-100 rotate-0'}`}>
              <Icon className="w-8 h-8 text-white" />
              {/* Glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-r ${currentStepData.color} rounded-2xl opacity-20 blur-xl animate-pulse`} />
            </div>
          </div>

          {/* Step counter */}
          <div className="text-center">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
              {isLastStep ? tr('Final Step', 'Dernière étape') : tr(`Step ${currentStep + 1} of ${onboardingSteps.length}`, `Étape ${currentStep + 1} sur ${onboardingSteps.length}`)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              {currentStepData.title}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              {currentStepData.description}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSkip}
              className="flex-1 py-3 px-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              {tr('Skip Tutorial', 'Passer le tutoriel')}
            </button>
            <button
              onClick={handleNext}
              className={`flex-1 py-3 px-4 text-sm font-bold text-white rounded-xl bg-gradient-to-r ${currentStepData.color} hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2`}
            >
              {isLastStep ? (
                <>
                  <Zap className="w-4 h-4" />
                  {tr('Start Creating', 'Commencer à créer')}
                </>
              ) : (
                <>
                  {tr('Got it', 'Compris')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Fun fact for engagement */}
          {!isLastStep && (
            <div className="mt-4 p-3 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 text-center">
                💡 <strong>{tr('Tip:', 'Astuce :')}</strong> {tr('Standard users can generate unlimited story variations!', "Les utilisateurs Standard peuvent générer des variations d'histoires illimitées !")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StandardOnboardingModal;